import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Stack,
    Snackbar,
    useTheme,
    useMediaQuery,
    BottomNavigation,
    BottomNavigationAction
} from '@mui/material';
import {
    RequestQuote,
    Schedule,
    ChecklistRtl,
    Add,
    Assessment
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import MobileBottomBar from '../components/MobileBottomBar';
import apiClient from '../api';

// Import form components (we'll create these next)
import TimeOffRequestForm from '../components/forms/TimeOffRequestForm';
import TimeOffRequestList from '../components/forms/TimeOffRequestList';
import ApparelOrderForm from '../components/forms/ApparelOrderForm';
import ApparelOrderList from '../components/forms/ApparelOrderList';
import WeeklyChecklistForm from '../components/forms/WeeklyChecklistForm';
import WeeklyChecklistList from '../components/forms/WeeklyChecklistList';

function FormsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [submissionNotification, setSubmissionNotification] = useState('');
    const [timeOffRefreshTrigger, setTimeOffRefreshTrigger] = useState(0);
    const [newTimeOffItem, setNewTimeOffItem] = useState(null);
    const [checklistRefreshTrigger, setChecklistRefreshTrigger] = useState(0);
    const [checklistEditItem, setChecklistEditItem] = useState(null);
    const searchParams = new URLSearchParams(location.search);
    const quickOpen = searchParams.get('quick') === '1' || searchParams.get('quick') === 'true' || location.state?.quickOpen;
    const path = location.pathname;
    const isFormRoute = path.includes('/forms/time-off') || path.includes('/forms/apparel') || path.includes('/forms/weekly-checklist');
    

    // Determine active tab based on current path
    useEffect(() => {
        if (path.includes('/forms/time-off')) {
            setActiveTab(1);
        } else if (path.includes('/forms/apparel')) {
            setActiveTab(2);
        } else if (path.includes('/forms/weekly-checklist')) {
            setActiveTab(3);
        } else {
            setActiveTab(0); // Default to overview
        }
    }, [path]);

    // Fetch form statistics for overview
    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const response = await apiClient.get('/forms/stats');
                setStats(response.data);
            } catch (error) {
                console.error('Error fetching form statistics:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [refreshTrigger]); // Refetch stats when refresh is triggered

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Always scroll to top when switching destinations
        try {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (_) {
            window.scrollTo(0, 0);
        }
        
        // Navigate to appropriate route
        switch (newValue) {
            case 0:
                navigate('/forms');
                break;
            case 1:
                navigate('/forms/time-off');
                break;
            case 2:
                navigate('/forms/apparel');
                break;
            case 3:
                navigate('/forms/weekly-checklist');
                break;
            default:
                navigate('/forms');
        }
    };

    // Handle successful checklist submission
    const handleChecklistSubmitted = (newChecklist) => {
        console.log('[FormsPage] Checklist submitted successfully:', newChecklist);
        
        // Trigger refresh of both stats and list
        setRefreshTrigger(prev => prev + 1);
        setChecklistRefreshTrigger(prev => prev + 1);
        
        // Show notification
        setSubmissionNotification('Weekly checklist submitted successfully and is now visible in your checklist history below.');
        // Clear any edit item after submission
        setChecklistEditItem(null);
    };

    // Handle successful time off submission  
    const handleTimeOffSubmitted = (newRequest) => {
        console.log('[FormsPage] Time off request submitted successfully:', newRequest);
        // Trigger overview stats refresh
        setRefreshTrigger(prev => prev + 1);
        // Trigger list refresh for mobile (where list may remount) and pass item for instant prepend on desktop
        setTimeOffRefreshTrigger(prev => prev + 1);
        setNewTimeOffItem(newRequest);
        setSubmissionNotification('Time off request submitted successfully.');
    };

    // Handle successful apparel order submission
    const handleApparelOrderSubmitted = (newOrder) => {
        console.log('[FormsPage] Apparel order submitted successfully:', newOrder);
        setRefreshTrigger(prev => prev + 1);
        // Trigger list refresh and instant prepend
        setSubmissionNotification('Apparel order submitted successfully.');
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
            case 'needs_attention':
                return 'warning';
            case 'approved':
            case 'satisfactory':
                return 'success';
            case 'rejected':
            case 'critical':
                return 'error';
            default:
                return 'default';
        }
    };

    // Listen for edit events from the list to load into form
    useEffect(() => {
        const handler = (e) => {
            const item = e.detail;
            console.log('[FormsPage] Received edit event for checklist', item?._id);
            setChecklistEditItem(item);
            try {
                document.getElementById('weekly-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (_) {}
        };
        window.addEventListener('weeklyChecklist:edit', handler);
        return () => window.removeEventListener('weeklyChecklist:edit', handler);
    }, []);

    const FormsOverview = () => (
        <Box>
            <Typography variant="h5" gutterBottom>
                Forms Center Overview
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Submit and track various forms including time off requests, apparel orders, and weekly safety checklists.
            </Typography>

            {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={1.5}>
                    {/* Time Off Requests Card */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ 
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' }
                        }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                    <Schedule color="primary" fontSize="large" />
                                    <Typography variant="h6">Time Off Requests</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Submit vacation, sick leave, and other time-off requests for approval.
                                </Typography>
                                
                                {stats?.timeOff && (
                                    <Box mb={2}>
                                        <Typography variant="subtitle2" gutterBottom>Status Summary:</Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {stats.timeOff.map((stat) => (
                                                <Chip
                                                    key={stat._id}
                                                    label={`${stat._id}: ${stat.count}`}
                                                    size="small"
                                                    color={getStatusColor(stat._id)}
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                                
                                <Button 
                                    variant="contained" 
                                    startIcon={<Add />}
                                    onClick={() => navigate('/forms/time-off')}
                                    fullWidth
                                >
                                    Submit Request
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Apparel Orders Card */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ 
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' }
                        }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                    <RequestQuote color="primary" fontSize="large" />
                                    <Typography variant="h6">Apparel Orders</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Order company apparel including shirts, hats, jackets, and more.
                                </Typography>
                                
                                {stats?.apparel && (
                                    <Box mb={2}>
                                        <Typography variant="subtitle2" gutterBottom>Status Summary:</Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {stats.apparel.map((stat) => (
                                                <Chip
                                                    key={stat._id}
                                                    label={`${stat._id}: ${stat.count}`}
                                                    size="small"
                                                    color={getStatusColor(stat._id)}
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                                
                                <Button 
                                    variant="contained" 
                                    startIcon={<Add />}
                                    onClick={() => navigate('/forms/apparel')}
                                    fullWidth
                                >
                                    Place Order
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Weekly Checklists Card */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ 
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' }
                        }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                    <ChecklistRtl color="primary" fontSize="large" />
                                    <Typography variant="h6">Weekly Checklists</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Submit your week’s operational checklist: odometer readings, weekly metrics, and operational details.
                                </Typography>
                                
                                {stats?.checklists && (
                                    <Box mb={2}>
                                        <Typography variant="subtitle2" gutterBottom>Status Summary:</Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {/* Derived badges: Pending Review vs Reviewed */}
                                            {Array.isArray(stats.checklists) && (
                                                <>
                                                    <Chip
                                                        label={`Pending Review: ${stats.checklists.find(s=>s._id==='pending_review')?.count || 0}`}
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={`Reviewed: ${stats.checklists.find(s=>s._id==='reviewed')?.count || 0}`}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                </>
                                            )}
                                        </Stack>
                                    </Box>
                                )}
                                
                                <Button 
                                    variant="contained" 
                                    startIcon={<Add />}
                                    onClick={() => navigate('/forms/weekly-checklist')}
                                    fullWidth
                                >
                                    Submit Checklist
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Admin Section */}
            {(user?.role === 'admin' || user?.role === 'company_admin') && (
                <Box mt={4}>
                    <Paper sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Assessment color="secondary" />
                            <Typography variant="h6">Administrative Actions</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Review and manage submitted forms from your team.
                        </Typography>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Use the individual form sections to review pending submissions and update statuses.
                        </Alert>
                    </Paper>
                </Box>
            )}
        </Box>
    );

    const phoneCompanyUser = isMobile && user?.role === 'company_user';
    const showFormsBar = isMobile && !phoneCompanyUser;
    if (phoneCompanyUser && (path === '/forms' || path === '/forms/')) {
        return <Navigate to="/forms/weekly-checklist" replace />;
    }

    return (
        <Box sx={{ pt: 0, pb: showFormsBar ? 'calc(56px + env(safe-area-inset-bottom, 0px))' : 0 }}>
            {/* Header (hidden on mobile when in a specific form route) */}
            {!(isMobile && isFormRoute) && (
                <Paper sx={{ p: 1, mb: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Forms Center
                    </Typography>
                </Paper>
            )}

            {/* Navigation Tabs (hidden on mobile when in a specific form route) */}
            {!(isMobile && isFormRoute) && (
                <Paper sx={{ mb: 1.5, borderRadius: 2 }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab label="Overview" />
                        <Tab label="Time Off Requests" />
                        <Tab label="Apparel Orders" />
                        <Tab label="Weekly Checklists" />
                    </Tabs>
                </Paper>
            )}

            {/* Content Area */}
            <Routes>
                <Route index element={<FormsOverview />} />
                <Route path="time-off" element={
                    <Box>
                        <Box id="timeoff-form">
                        <TimeOffRequestForm 
                            onSubmitSuccess={handleTimeOffSubmitted} 
                            defaultOpen={isMobile || !!quickOpen}
                            compact={isMobile}
                        />
                        </Box>
                        <Box id="timeoff-list">
                        <TimeOffRequestList 
                            refreshTrigger={timeOffRefreshTrigger}
                            prependItem={newTimeOffItem}
                        />
                        </Box>
                    </Box>
                } />
                <Route path="apparel" element={
                    <Box>
                        <Box id="apparel-form">
                        <ApparelOrderForm 
                            onSubmitSuccess={handleApparelOrderSubmitted} 
                            defaultOpen={isMobile || !!quickOpen}
                            compact={isMobile}
                        />
                        </Box>
                        <Box id="apparel-list">
                        <ApparelOrderList refreshTrigger={refreshTrigger} prependItem={null} />
                        </Box>
                    </Box>
                } />
                <Route path="weekly-checklist" element={
                    <Box>
                        <Box id="weekly-form">
                        <WeeklyChecklistForm 
                            onSubmitSuccess={handleChecklistSubmitted} 
                            defaultOpen={isMobile || !!quickOpen}
                            compact={isMobile}
                            initialData={checklistEditItem}
                        />
                        </Box>
                        <Box id="weekly-list">
                        <WeeklyChecklistList refreshTrigger={checklistRefreshTrigger} />
                        </Box>
                    </Box>
                } />
            </Routes>

            {/* Snackbar for notifications */}
            <Snackbar
                open={!!submissionNotification}
                autoHideDuration={6000}
                onClose={() => setSubmissionNotification('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={() => setSubmissionNotification('')} severity="success" sx={{ width: '100%' }}>
                    {submissionNotification}
                </Alert>
            </Snackbar>
            {/* Bottom navigation for mobile admins. Company users use the layout bar. */}
            {showFormsBar && (
                <MobileBottomBar>
                    <BottomNavigation
                        showLabels
                        value={activeTab}
                        onChange={(e, newValue) => handleTabChange(e, newValue)}
                    >
                        <BottomNavigationAction label="Overview" icon={<Assessment />} />
                        <BottomNavigationAction label="Time Off" icon={<Schedule />} />
                        <BottomNavigationAction label="Apparel" icon={<RequestQuote />} />
                        <BottomNavigationAction label="Weekly" icon={<ChecklistRtl />} />
                    </BottomNavigation>
                </MobileBottomBar>
            )}
        </Box>
    );
}

export default FormsPage; 